import { useState } from 'react';
import { apiJson } from '../api/client.js';
import { assignmentUi } from '../i18n/assignmentUi.js';
import { t } from '../i18n/translate.js';
import { PROJECT_ROLES } from '../utils/taskQueue.js';
import { SearchableSelect } from './SearchableSelect.jsx';

export function ProjectTeamPanel({ projectId, members = [], staff = [], locale, onChange }) {
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('designer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const add = async (event) => {
    event.preventDefault();
    if (!userId) return;
    setBusy(true);
    setError('');
    try {
      await apiJson(`/api/admin/projects/${projectId}/members`, {
        method: 'POST',
        body: { user_id: Number(userId), role },
      });
      setUserId('');
      onChange?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (member, nextRole) => {
    setError('');
    try {
      await apiJson(`/api/admin/projects/${projectId}/members/${member.id}`, {
        method: 'PUT',
        body: { role: nextRole || null },
      });
      onChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (member) => {
    setError('');
    try {
      await apiJson(`/api/admin/projects/${projectId}/members/${member.id}`, { method: 'DELETE' });
      onChange?.();
    } catch (err) {
      setError(err.message);
    }
  };

  const taken = new Set(members.map((member) => String(member.user_id || member.user?.id)));

  return (
    <section className="panel" style={{ marginBottom: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>{t(assignmentUi.projectTeam, locale)}</h3>
      {error ? <p className="err">{error}</p> : null}
      {members.length ? (
        <ul className="list-plain" style={{ margin: 0, padding: 0 }}>
          {members.map((member) => (
            <li key={member.id} className="row-actions" style={{ justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.4rem' }}>
              <strong>{member.user?.name || 'Team member'}</strong>
              <SearchableSelect aria-label="Role" value={member.role || ''} onChange={(event) => void changeRole(member, event.target.value)}>
                <option value="">Role…</option>
                {PROJECT_ROLES.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </SearchableSelect>
              <button type="button" className="btn-link" onClick={() => void remove(member)}>Remove</button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted small">No one is on this project yet.</p>
      )}
      <form onSubmit={add} className="row-actions" style={{ marginTop: '0.75rem', alignItems: 'end' }}>
        <label className="field">
          <span>Add someone</span>
          <SearchableSelect value={userId} onChange={(event) => setUserId(event.target.value)}>
            <option value="">Choose…</option>
            {staff.filter((person) => !taken.has(String(person.id))).map((person) => (
              <option key={person.id} value={person.id}>{person.name}</option>
            ))}
          </SearchableSelect>
        </label>
        <label className="field">
          <span>Role</span>
          <SearchableSelect value={role} onChange={(event) => setRole(event.target.value)}>
            {PROJECT_ROLES.map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </SearchableSelect>
        </label>
        <button type="submit" className="btn-primary" disabled={busy || !userId}>
          {busy ? 'Adding…' : 'Add'}
        </button>
      </form>
    </section>
  );
}
